import { Button, Modal, NumberInput, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpdateInitialBalance } from 'src/Hooks/Mutations/useUpdateInitialBalance';

import styles from './EditInitialBalanceModal.module.scss';
import { Props } from './EditInitialBalanceModal.types';

interface FormValues {
  amount: number;
  label: string;
  reason: string;
}

const EditInitialBalanceModal = ({ open, onClose, assetId, initialBalance }: Props) => {
  const { t } = useTranslation();
  const { mutate, isLoading } = useUpdateInitialBalance({ onSuccess: onClose });

  const form = useForm<FormValues>({
    initialValues: { amount: 0, label: '', reason: '' },
  });

  useEffect(() => {
    if (open && initialBalance) {
      form.setValues({
        amount: initialBalance.amount,
        label: initialBalance.label ?? '',
        reason: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialBalance]);

  const handleSave = () => {
    if (!initialBalance) return;
    mutate({
      assetId,
      id: initialBalance.id,
      data: {
        amount: form.values.amount,
        label: form.values.label || undefined,
        reason: form.values.reason,
      },
    });
  };

  const canSave = form.values.reason.trim().length >= 5;

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={t('expenses.editInitialBalance.title', 'Modifier le solde initial')}
      size="md"
    >
      {initialBalance && (
        <Stack gap="md">
          <Text size="sm" fw={600}>
            {initialBalance.firstName} {initialBalance.lastName}
          </Text>
          <Text size="xs" c="dimmed" td="line-through">
            {t('expenses.editInitialBalance.previousAmount', 'Ancien solde : {{amount}} €', {
              amount: initialBalance.amount,
            })}
          </Text>

          <NumberInput
            label={t('expenses.editInitialBalance.amount', 'Nouveau solde (€)')}
            value={form.values.amount}
            onChange={value => form.setFieldValue('amount', typeof value === 'number' ? value : 0)}
            decimalScale={2}
          />

          <TextInput
            label={t('expenses.editInitialBalance.label', 'Libellé (optionnel)')}
            value={form.values.label}
            onChange={e => form.setFieldValue('label', e.currentTarget.value)}
          />

          <Textarea
            label={t('expenses.editInitialBalance.reason', 'Raison de la modification')}
            required
            minRows={2}
            value={form.values.reason}
            onChange={e => form.setFieldValue('reason', e.currentTarget.value)}
            placeholder={t(
              'expenses.editInitialBalance.reasonPlaceholder',
              'Ex : erreur de saisie initiale, justificatif retrouvé...'
            )}
          />

          <div className={styles.buttons}>
            <Button variant="outline" onClick={onClose}>
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button onClick={handleSave} loading={isLoading} disabled={!canSave}>
              {t('common.save', 'Enregistrer')}
            </Button>
          </div>
        </Stack>
      )}
    </Modal>
  );
};

export default EditInitialBalanceModal;
